import { formatSize, parseGeometryStyleHash, poiInfoToJson, zoomToMercatorsPerPixel } from "./utils";
import { IDefPosition, IPoiData, TWasmEventHoverType, IMapStyle } from "./interfaces";
import { deferPromise } from "~/util/general";
import { ILonLatPosition, IXYPosition, TWasmId, TWasmOverlay } from "~/interfaces";
import WasmMapMarker from "./entities/wasm-map-marker";
import WasmMapGeometry from "./entities/wasm-map-geometry";
import { IWasmStoreGeometry, IWasmStoreMarker } from "./store/interfaces";
import { debounce } from "~/lib/debounce";
import WasmTextures from "./textures";
import { DEF_DPIS, LAYER_MAPPING, LAYER_MAPPING_NUMB, MOBILE_TARGET_FPS, OPHOTO_YEAR_SRC_MAPPING, DEF_POSITION, TILT_DATA } from "./const";
import { registerTextures } from "./textures-set";
import { bindWasmTextureUrlProvider } from "./texture-url-provider";
import WasmMapMarkerSharedPosition from "./entities/wasm-map-marker-shared-position";
import { isWasmEnabled } from "./feature-detection";
import { createMapBestfit } from "~/ui/map/bestfit";
import WasmScroll from "./wasm-scroll";

export type TWasmMapClick = {
	longitude: number;
	latitude: number;
	wasmId: TWasmId;
	pois: Array<IPoiData>;
	ctrlKey: boolean;
	shiftKey: boolean;
	altKey: boolean;
	metaKey: boolean;
	button: number;
	pointerType: string;
	clientX: number;
	clientY: number;
	contactX: number;
	contactY: number;
	rasterLayer: string;
};

export type TWasmMapHover = {
	longitude: number;
	latitude: number;
	type: TWasmEventHoverType;
	wasmId: TWasmId;
	poi: IPoiData;
	clientX: number;
	clientY: number;
	contactX: number;
	contactY: number;
	rasterLayer: string;
};

interface IScreenshot {
	data: string;
	width: number;
	height: number;
}

interface IWasmOpts {
	parentElem: HTMLElement;
	canvasQuery?: string;
	urlReplacement?: {
		fromUrl: string;
		toUrl: string;
	};
	animTo?: number;
	moveTo?: number;
	dpi?: number;
	loadTo?: number;
	urls?: {
		poiAdmin: string;
		styles: string;
	};
	position?: IDefPosition;
	mapStyle?: IMapStyle;
	release?: boolean;
	isMobile?: boolean;
	clickDistance?: number;
	dblClickTo?: number;
	onMapClick?: (data: TWasmMapClick) => void;
	onHover?: (data: TWasmMapHover) => void;
	onContextMenu?: (event: PointerEvent, poisData: Array<IPoiData>, wasmId: number) => void;
	onDblClick?: (event: PointerEvent) => void;
	onMapRedraw?: () => void;
	onFps?: (fps: number) => void;
	onDragStart?: (wasmId: TWasmId, event: PointerEvent) => number;
	onDragMove?: (id: number, event: PointerEvent) => void;
	onDragEnd?: (id: number, event: PointerEvent) => void;
	onBestfit?: (coordinates: Array<[number, number]>) => void;
}

const ALL_OVERLAYS: Array<TWasmOverlay> = ["hillshade", "hillslope", "knLayer", "panoLayer", "touristMode"];
const MAX_PU_EVENTS = 2;
const bestfitDiv = createMapBestfit();
let bestfitStartX = 0;
let bestfitStartY = 0;

export default class Wasm {
	#opts: IWasmOpts;

	#module: typeof Module;

	#mapControl: Module.MapControl;

	#mapContent: Module.ContentService;

	#canvas: HTMLCanvasElement;

	#bindUpdateMapContent: () => void;

	#icons: Map<string, number>;

	#loadData: {
		to: number;
	} & ReturnType<typeof deferPromise>;

	#initProm: ReturnType<typeof deferPromise>;

	#lockMap: boolean;

	#screenshotPromise: null | ReturnType<typeof deferPromise<IScreenshot>>;

	#activePointers: Map<number, PointerEvent>;

	#lastHoverRasterLayer: string;

	#lastHoverElementId: number;

	#dragElementId: number;

	#lastHoverPoi: IPoiData;

	#lastPos: IXYPosition;

	#moveState: boolean;

	#textures: WasmTextures;

	#lastIconId: number;

	/** probiha prave drag & drop */
	#dragging: boolean;

	#lineStyles: { [key: string]: Module.LineStyle };

	#stats: Module.Stats;

	/** probiha prave nastavovani rozmeru pro bestfit */
	#fitting: boolean;

	#tiltData: {
		pos: IXYPosition;
		isActive: boolean;
		shift: boolean;
		current: number;
	};

	#loaded: boolean;

	#lastPUEvents: Array<{ time: number; event: PointerEvent; }>;

	constructor(opts?: IWasmOpts) {
		this.#opts = {
			parentElem: null,
			canvasQuery: "canvas",
			animTo: 400,
			loadTo: 333,
			moveTo: 50,
			isMobile: false,
			dpi: window.devicePixelRatio,
			position: DEF_POSITION,
			mapStyle: null,
			urls: {
				poiAdmin: "https://vectmap.mapy.cz/rpc/",
				styles: "/wasm/styles/",
			},
			clickDistance: 3,
			dblClickTo: 350,
			release: false,
			onMapRedraw: () => {},
			onFps: () => {},
			onDblClick: () => {},
			...opts,
		};
		this.#mapControl = null;
		this.#canvas = document.querySelector(this.#opts.canvasQuery);
		this.#bindUpdateMapContent = this.#updateMapContent.bind(this);
		// tady je to pouze ukazatel, nevraci se to a nenastavuje se to
		this.#icons = new Map<string, number>();
		this.#lastIconId = 0;
		this.#dragging = false;
		this.#loadData = {
			to: 0,
			...deferPromise(),
		};
		this.#initProm = deferPromise();
		this.#lockMap = false;
		this.#screenshotPromise = null;
		this.#activePointers = new Map();
		this.#lastHoverRasterLayer = "";
		this.#lastHoverPoi = null;
		this.#lastHoverElementId = 0;
		this.#lastPos = {
			x: 0,
			y: 0,
		};
		this.#moveState = false;
		this.#textures = new WasmTextures(this.#opts.dpi);
		this.#lineStyles = {};
		this.#stats = {
			fps: 0,
			drawBuffersSize: 0,
			drawCnt: 0,
			frameIdx: 0,
			memUsedSize: 0,
			roCnt: 0,
			texMemSize: 0,
			triCnt: 0,
		};
		this.#tiltData = {
			isActive: false,
			pos: null,
			shift: false,
			current: TILT_DATA.angles[0],
		};
		this.#loaded = false;
		this.#lastPUEvents = [];

		this.#load();
	}

	destroy() {
		window.removeEventListener("keydown", this);
		window.removeEventListener("keyup", this);
		this.#mapControl.disconnect();
	}

	handleEvent(event: KeyboardEvent) {
		switch (event.type) {
			case "keydown":
				if (event.key === "Shift") {
					this.#tiltData.shift = true;
				}
				break;

			case "keyup":
				if (event.key === "Shift") {
					this.#tiltData.shift = false;
				}
				break;

			default:
		}
	}

	#getModuleConf() {
		return {
			printErr: (...args: any) => {
				if (args.length > 1) {
					// eslint-disable-next-line no-console
					console.log(args.join(" "));
				} else {
					// eslint-disable-next-line no-console
					console.log(args[0]);
				}
			},
			totalDependencies: 0,
			monitorRunDependencies: (left: number) => {
				if (this.#loadData.to) {
					clearTimeout(this.#loadData.to);
					this.#loadData.to = 0;
				}

				this.#loadData.to = setTimeout(() => {
					if (left === 0) {
						this.#loadData.resolve({});
					}
				}, this.#opts.loadTo) as unknown as number;
			},
			locateFile: (file: string) => `/wasm/${file}`,
		};
	}

	async #load() {
		const wasmEnabled = await isWasmEnabled();
		const jsFallback = !wasmEnabled;
		const libSznMaps = this.#opts.release
			? jsFallback
				// @ts-ignore
				? (await import(/* webpackIgnore: true */ "../../wasm/release/lib-maps-js.js")).default
				: (await import(/* webpackIgnore: true */ "../../wasm/release/lib-maps.js")).default
			: jsFallback
				// @ts-ignore
				? (await import(/* webpackIgnore: true */ "../../wasm/develop/lib-maps-js.js")).default
				: (await import(/* webpackIgnore: true */ "../../wasm/develop/lib-maps.js")).default;
		/* eslint-disable-next-line */
		const loadProm = libSznMaps(this.#getModuleConf());

		const [module] = await Promise.all([
			loadProm,
			this.#textures.load(),
		]);

		this.#setModule(module as typeof Module);
	}

	#setModule(module: typeof Module) {
		this.#module = module;

		module.setLogLevel(this.#opts.release ? module.LogLevel.None : module.LogLevel.Error);

		// create poi admin cache, wich resolves poi iconst for map / page
		const poiAdmin = new module.PoiAdminCache(this.#opts.urls.poiAdmin);

		// create map
		const mapControl = new module.MapControl(this.#opts.isMobile) as Module.MapControl;

		// souradnice
		mapControl.setPositionWgs({
			x: this.#opts.position.lon,
			y: this.#opts.position.lat,
		});
		mapControl.setZoom(this.#opts.position.zoom);

		if (this.#opts.urlReplacement) {
			const { fromUrl, toUrl } = this.#opts.urlReplacement;
			const mapHostsSetup = new module.MapHostsSetup();

			mapHostsSetup.setUrlReplacement(fromUrl, toUrl, false);
			mapControl.setMapHostsSetup(mapHostsSetup);
		}

		this.#mapControl = mapControl;

		if (this.#opts.isMobile) {
			this.#mapControl.setTargetFps(MOBILE_TARGET_FPS);
		}

		const moveCb = debounce(() => {
			if (this.#moveState) {
				this.#moveState = false;
			} else {
				this.#opts.onMapRedraw();
			}
		}, this.#opts.moveTo);

		mapControl.setOnMapViewChanged(() => {
			moveCb();
		});

		// setup poi icon resolver used by map - poiAdmin class
		mapControl.setCustomPoiIconResolver(poiAdmin);

		// initialize map cotent service
		this.#initMapContentService(mapControl.getContentService());

		// load styles from server path & show them in console, select default stype set after load
		// todor this.#opts.dpi - nechame 1
		mapControl.loadStyles(this.#opts.urls.styles, DEF_DPIS, window.devicePixelRatio < 1.5 ? 1 : 2, () => {
			// mame styly?
			if (this.#opts.mapStyle) {
				this.setMapstyle(this.#opts.mapStyle);
			} else {
				this.#mapControl.setDefaultStyleSet();
			}

			// nacteno
			this.#loaded = true;
		});

		// connect map to canvas & update size
		mapControl.connectToCanvas(this.#opts.canvasQuery);

		this.#canvas.tabIndex = -1;
		// send mouse events to map
		this.#canvas.addEventListener("contextmenu", event => {
			event.preventDefault();
			this.#mapControl.onClick(event.clientX, event.clientY, { action: "contextmenu", event: event });
		});

		// update size of map when resise
		window.addEventListener("resize", () => {
			this.#updateMapControlSize();
		});

		// update size of map now
		this.#updateMapControlSize();

		// start render loop
		window.requestAnimationFrame(this.#bindUpdateMapContent);

		mapControl.setOnRenderStats(stats => {
			this.#stats = stats;
			this.#opts.onFps(stats.fps);
		});

		this.#initMoveControls();

		this.#initKeyboard();

		poiAdmin.delete();

		this.#initProm.resolve({});
	}

	get loadProm() {
		return Promise.all([
			this.#loadData.promise,
			this.#initProm.promise,
		]);
	}

	set lockMap(lockMap: boolean) {
		this.#lockMap = lockMap;
	}

	syncPort() {
		this.#updateMapControlSize();
	}

	createMarker(markerData: IWasmStoreMarker) {
		// specialni typ
		if (markerData.type === "position-share" || markerData.type === "position-share-arrow") {
			return new WasmMapMarkerSharedPosition(this, markerData);
		}

		return new WasmMapMarker(this, markerData);
	}

	createGeometry(geomData: IWasmStoreGeometry) {
		return new WasmMapGeometry(this, geomData);
	}

	// ziskani stylu - pokud neexistuje, vytvorime jej
	createGeometryStyle(styleHash: string) {
		if (!(styleHash in this.#lineStyles)) {
			const styleData = parseGeometryStyleHash(styleHash);
			let lineCap: Module.LineCapType = this.#module.LineCapType.Butt;
			let linJoin: Module.LineJointType = this.#module.LineJointType.Bevel;

			switch (styleData.lineCap) {
				case "butt":
					lineCap = this.#module.LineCapType.Butt;
					break;

				case "round":
					lineCap = this.#module.LineCapType.Round;
					break;

				case "square":
					lineCap = this.#module.LineCapType.Square;
					break;

				default:
			}

			switch (styleData.lineJoin) {
				case "bevel":
					linJoin = this.#module.LineJointType.Bevel;
					break;

				case "round":
					linJoin = this.#module.LineJointType.Round;
					break;

				case "miter":
					linJoin = this.#module.LineJointType.Miter;
					break;

				default:
			}

			const styleInst = new this.#module.LineStyle(
				styleData.width,
				styleData.color,
				styleData.edgeWidth,
				styleData.edgeColor,
				styleData.keepExactWidth,
				lineCap,
				linJoin,
				styleData.dashed,
			);

			this.#lineStyles[styleHash] = styleInst;
		}

		return this.#lineStyles[styleHash];
	}

	setMapstyle({
		base,
		overlays = [],
		variant = "",
		ophotoTimeline = "",
		trafficOverlay = false,
		dpiScale,
	}: IMapStyle) {
		const wasmName = LAYER_MAPPING[base];
		const styleSet = new this.#module.StyleSetCfg(wasmName, variant);
		const poiSets: Array<string> = [];

		// set styleset attributes values
		for (const overlay of ALL_OVERLAYS) {
			styleSet.setAttribute(overlay, overlays.includes(overlay) ? "true" : "false");
		}

		// pro katastr ridime poie v lookupu
		if (overlays.includes("knLayer")) {
			poiSets.push("cadastral");
			poiSets.push(LAYER_MAPPING_NUMB[base].toString());
		}

		if (trafficOverlay) {
			poiSets.push(LAYER_MAPPING_NUMB.dopravni.toString());
			poiSets.push(LAYER_MAPPING_NUMB[base].toString());
		}

		if (poiSets.length > 0) {
			styleSet.setAttribute("poiSets", poiSets.join(","));
		}

		if (typeof dpiScale === "number") {
			styleSet.setAttribute("dpiScale", dpiScale.toFixed(1));
		}

		// modifikator ophoto
		if (ophotoTimeline) {
			const src = OPHOTO_YEAR_SRC_MAPPING[ophotoTimeline];

			if (src) {
				styleSet.setAttribute("ophotoYearSrc", src);
			}
		}

		// set styleset config to map
		this.#mapControl.setStyleSet(styleSet);

		// delete after use
		styleSet.delete();
	}

	getScreenshot() {
		this.#screenshotPromise = deferPromise<IScreenshot>();
		this.#mapControl.requestFrame();

		return this.#screenshotPromise.promise;
	}

	setCenterZoom(wgsX: number, wgsY: number, zoom?: number) {
		this.#moveState = true;
		this.#mapControl.setPositionWgs({
			x: wgsX,
			y: wgsY,
		});
		!isNaN(zoom) && this.#mapControl.setZoom(zoom);
	}

	setCenterZoomAnim(wgsX: number, wgsY: number, zoom: number, animMs = this.#opts.animTo) {
		const merc = this.module.GeoMath.wgsToMercator({
			x: wgsX,
			y: wgsY,
		});

		this.mapControl.animToPosAndMPP(merc, zoomToMercatorsPerPixel(zoom), animMs);
	}

	zoomToPixel(pixel: IXYPosition, zoom: number, animTo?: number) {
		const setupData = this.mapControl.createMapViewSetupToZoomAt(pixel, zoom);

		this.mapControl.animToMapView(setupData, animTo ?? this.#opts.animTo, this.module.EasingType.Linear);
	}

	getCenterZoom() {
		const coords = this.#mapControl.getPositionWgs();
		const zoom = parseFloat(this.#mapControl.getZoom().toFixed(2));

		return {
			lon: coords.x,
			lat: coords.y,
			zoom,
		};
	}

	getZoom() {
		return this.#mapControl.getZoom();
	}

	coordsFromEvent(event: PointerEvent): IXYPosition {
		const { clientX, clientY } = event;

		return this.coordsFromScreenPosition(clientX, clientY);
	}

	coordsFromScreenPosition(clientX, clientY): IXYPosition {
		const pos = this.#mapControl.screenPosToMapPos({
			x: clientX,
			y: clientY,
		});
		const wgs = this.#module.GeoMath.mercatorToWGS(pos);

		return wgs;
	}

	coordsToPixel(wgs84: IXYPosition) {
		const merc = this.#module.GeoMath.wgsToMercator(wgs84);

		return this.#mapControl.mapPosToScreenPos(merc);
	}

	redraw() {
		this.#mapControl?.requestFrame();
	}

	get textures() {
		return this.#textures;
	}

	get icons() {
		return this.#icons;
	}

	get lineStyles() {
		return this.#lineStyles;
	}

	get module() {
		return this.#module;
	}

	get mapControl() {
		return this.#mapControl;
	}

	get mapContent() {
		return this.#mapContent;
	}

	get stats() {
		const output: any = {
			...this.#stats,
		};

		output.drawBuffersSize = formatSize(output.drawBuffersSize);
		output.texMemSize = formatSize(output.texMemSize);
		output.memUsedSize = formatSize(output.memUsedSize);

		return output;
	}

	getViewport(): null | { lb: ILonLatPosition; rt: ILonLatPosition;} {
		const width = this.#canvas.width / this.#opts.dpi;
		const height = this.#canvas.height / this.#opts.dpi;

		if (!this.#mapControl) {
			return null;
		}

		const rtPosMerc = this.#mapControl.screenPosToMapPos({
			x: width,
			y: 0,
		});
		const lbPosMerc = this.#mapControl.screenPosToMapPos({
			x: 0,
			y: height,
		});
		const rtWgs = this.#module.GeoMath.mercatorToWGS(rtPosMerc);
		const lbWgs = this.#module.GeoMath.mercatorToWGS(lbPosMerc);

		return {
			lb: {
				lon: lbWgs.x,
				lat: lbWgs.y,
			},
			rt: {
				lon: rtWgs.x,
				lat: rtWgs.y,
			},
		};
	}

	getTextureData(nameId: string): {
		id: number;
		exists: boolean;
	} {
		const id = this.#icons.get(nameId);

		if (id) {
			return {
				id,
				exists: true,
			};
		}

		this.#lastIconId++;
		this.#icons.set(nameId, this.#lastIconId);

		return {
			id: this.#lastIconId,
			exists: false,
		};
	}

	#initMapContentService(contentService: Module.ContentService) {
		// setup global variable
		this.#mapContent = contentService;

		// textury
		registerTextures(this, this.#opts.dpi);
		// provider - reseni url pro textury
		bindWasmTextureUrlProvider(this);

		contentService.setOnClickCallback((clickData, actionUserData) => {
			if (!this.#loaded) {
				clickData?.delete();

				return;
			}

			const poisData: Array<IPoiData> = [];
			let rasterLayer = "";

			// vyplnime dat o POIich
			if (clickData?.DataType === this.#module.ActionDataType.Poi) {
				for (let ind = 0; ind < clickData.Pois.size(); ind++) {
					poisData.push(poiInfoToJson(clickData.Pois.get(ind)));
				}
			}

			if (clickData?.DataType === this.#module.ActionDataType.RasterLayerHit) {
				rasterLayer = clickData.rasterHitResult;
			}

			// kontextove menu
			if (actionUserData?.action === "contextmenu") {
				this.#opts.onContextMenu(actionUserData.event, poisData, clickData?.ElementId);
			} else if (actionUserData?.action === "click") {
				const wgs = this.coordsFromScreenPosition(actionUserData.event.clientX, actionUserData?.event.clientY);
				const contact = clickData?.ContactPos ? this.#mapControl.mapPosToScreenPos(clickData?.ContactPos) : { x: 0, y: 0 };

				this.#opts.onMapClick?.({
					pois: poisData,
					wasmId: clickData?.ElementId,
					longitude: wgs?.x ?? 0,
					latitude: wgs?.y ?? 0,
					clientX: actionUserData.event.clientX ?? 0,
					clientY: actionUserData.event.clientY ?? 0,
					contactX: contact.x,
					contactY: contact.y,
					ctrlKey: actionUserData.event.ctrlKey,
					shiftKey: actionUserData.event.shiftKey,
					altKey: actionUserData.event.altKey,
					metaKey: actionUserData.event.metaKey,
					button: actionUserData.event.button,
					pointerType: actionUserData.event.pointerType,
					rasterLayer,
				});
			}

			// smazeme data -> kvuli infu do konzole
			clickData?.delete();
		});

		contentService.setOnHoverCallback(hoverData => {
			if (!this.#loaded) {
				hoverData?.delete();

				return;
			}

			const newElementId = hoverData?.ElementId || 0;
			let newPoi: IPoiData = null;
			const wgs = this.coordsFromScreenPosition(this.#lastPos.x, this.#lastPos.y);
			const contact = hoverData?.ContactPos ? this.#mapControl.mapPosToScreenPos(hoverData?.ContactPos) : { x: 0, y: 0 };
			const commonData: Pick<TWasmMapHover, "longitude" | "latitude" | "clientX" | "clientY" | "contactX" | "contactY"> = {
				longitude: wgs?.x || 0,
				latitude: wgs?.y || 0,
				clientX: this.#lastPos.x,
				clientY: this.#lastPos.y,
				contactX: contact.x,
				contactY: contact.y,
			};
			let rasterLayer = "";

			if (hoverData?.DataType === this.#module.ActionDataType.Poi && hoverData?.Pois.size() > 0) {
				newPoi = poiInfoToJson(hoverData.Pois.get(0));
			}

			if (hoverData?.DataType === this.#module.ActionDataType.RasterLayerHit) {
				rasterLayer = hoverData.rasterHitResult;
			}

			// zadna zmena elementu a element je nastaveny - pohybujeme se porad po tom samem markeru
			if (this.#lastHoverElementId === newElementId && newElementId) {
				this.#opts.onHover({
					type: "move",
					wasmId: newElementId,
					poi: null,
					rasterLayer: "",
					...commonData,
				});

				// smazeme data -> kvuli infu do konzole
				hoverData?.delete();

				return;
			}

			// zadna zmena POie a POI je nastaveny - pohybujeme se porad po tom samem POIi
			if (this.#lastHoverPoi?.source === newPoi?.source && this.#lastHoverPoi?.id === newPoi?.id && newPoi) {
				this.#opts.onHover({
					type: "move",
					wasmId: 0,
					poi: newPoi,
					rasterLayer: "",
					...commonData,
				});

				// smazeme data -> kvuli infu do konzole
				hoverData?.delete();

				return;
			}

			// zadna zmena POie a POI je nastaveny - pohybujeme se porad po tom samem POIi
			if (this.#lastHoverRasterLayer === rasterLayer && rasterLayer) {
				this.#opts.onHover({
					type: "move",
					wasmId: 0,
					poi: null,
					rasterLayer,
					...commonData,
				});

				// smazeme data -> kvuli infu do konzole
				hoverData?.delete();

				return;
			}

			// nejdriv udelame oba potencialni leave
			this.#lastHoverElementId && this.#opts.onHover({
				type: "leave",
				wasmId: this.#lastHoverElementId,
				poi: null,
				rasterLayer: "",
				...commonData,
			});
			this.#lastHoverPoi && this.#opts.onHover({
				type: "leave",
				wasmId: 0,
				poi: this.#lastHoverPoi,
				rasterLayer: "",
				...commonData,
			});
			this.#lastHoverRasterLayer && this.#opts.onHover({
				type: "leave",
				wasmId: 0,
				poi: null,
				rasterLayer: this.#lastHoverRasterLayer,
				...commonData,
			});

			// a pak oba potencialni entery - jinak by se nam michalo poradi enter/leave a to by byl problem napriklad pro zmeny kurzoru
			newElementId && this.#opts.onHover({
				type: "enter",
				wasmId: newElementId,
				poi: null,
				rasterLayer: "",
				...commonData,
			});
			newPoi && this.#opts.onHover({
				type: "enter",
				wasmId: 0,
				poi: newPoi,
				rasterLayer: "",
				...commonData,
			});
			rasterLayer && this.#opts.onHover({
				type: "enter",
				wasmId: 0,
				poi: null,
				rasterLayer,
				...commonData,
			});

			// zapamatujeme si nova data
			this.#lastHoverElementId = newElementId;
			this.#lastHoverPoi = newPoi;
			this.#lastHoverRasterLayer = rasterLayer;

			// smazeme data -> kvuli infu do konzole
			hoverData?.delete();
		});
	}

	#updateMapControlSize() {
		const dpiScale = this.#opts.dpi;
		const width = this.#opts.parentElem.offsetWidth;
		const height = this.#opts.parentElem.offsetHeight;

		this.#canvas.style.width = `${width}px`;
		this.#canvas.style.height = `${height}px`;
		this.#canvas.width = width * dpiScale;
		this.#canvas.height = height * dpiScale;

		// eslint-disable-next-line no-console
		console.debug(`Canvas width ${this.#canvas.width} height ${this.#canvas.height}`);

		if (this.#mapControl) {
			this.#mapControl.setHostSize(width, height);
			this.#mapControl.setRenderResolution(this.#canvas.width, this.#canvas.height);
			window.requestAnimationFrame(this.#bindUpdateMapContent);
		}
	}

	#updateMapContent(timestamp: number) {
		if (this.#mapControl) {
			this.#mapControl.update(timestamp);

			if (this.#screenshotPromise) {
				const imgSrcData = this.#canvas.toDataURL();

				this.#screenshotPromise.resolve({
					data: imgSrcData,
					width: this.#canvas.width / this.#opts.dpi,
					height: this.#canvas.height / this.#opts.dpi,
				});
				this.#screenshotPromise = null;
			}

			window.requestAnimationFrame(this.#bindUpdateMapContent);
		}
	}

	#eventDown(event: PointerEvent) {
		// pokud tohle neudelame, tak bude nastavat blur na inputech, nez se dozvi, ze se pro ne kliklo do mapy
		event.preventDefault();
		this.#lastPos.x = event.clientX;
		this.#lastPos.y = event.clientY;

		if (this.#tiltData.shift && !this.#lockMap) {
			this.#tiltData.isActive = true;
			this.#tiltData.pos = {
				x: event.clientX,
				y: event.clientY,
			};

			return;
		}

		if (this.#mapControl && !this.#lockMap && (event.pointerType !== "mouse" || event.button === 0)) {
			this.#dragElementId = this.#lastHoverElementId ? this.#opts.onDragStart?.(this.#lastHoverElementId, event) ?? 0 : 0;

			// s CTRL nebo META zaciname delat obdelnik pro bestfit
			if ((event.ctrlKey || event.metaKey) && this.#activePointers.size === 0) {
				this.#fitting = true;
				bestfitStartX = event.clientX;
				bestfitStartY = event.clientY;
				bestfitDiv.style.left = `${bestfitStartX}px`;
				bestfitDiv.style.top = `${bestfitStartY}px`;
				bestfitDiv.style.width = `0px`;
				bestfitDiv.style.height = `0px`;
				document.body.appendChild(bestfitDiv);
			// pokud to dava smysl, zaciname s drag & drop
			} else if (this.#activePointers.size === 0 && this.#dragElementId) {
				this.#dragging = true;
			// jinak posleme data do mapy a ta si udela vlastni gesto, pravdepodobne pohyb mapou
			} else {
				this.#mapControl.onPointerDown(event.pointerId, event.clientX, event.clientY, {
					event: {
						ctrlKey: event.ctrlKey,
						shiftKey: event.shiftKey,
						altKey: event.altKey,
						metaKey: event.metaKey,
						button: event.button,
						pointerType: event.pointerType,
						clientX: event.clientX,
						clientY: event.clientY,
					},
				});
			}

			this.#activePointers.set(event.pointerId, event);
		}
	}

	#eventMove(event: PointerEvent) {
		this.#lastPos.x = event.clientX;
		this.#lastPos.y = event.clientY;

		if (this.#tiltData.isActive) {
			const dist = Math.sqrt(Math.pow(event.clientX - this.#tiltData.pos.x, 2) + Math.pow(event.clientY - this.#tiltData.pos.y, 2));
			const direction = event.clientY > this.#tiltData.pos.y
				? 1
				: -1;
			const percent = Math.min(dist / TILT_DATA.distance, 1);
			const anglesDistances = TILT_DATA.angles[1] - TILT_DATA.angles[0];
			const diffAngle = anglesDistances * percent;
			const newAngleUnc = this.#tiltData.current - (direction * diffAngle);
			const newAngle = Math.min(Math.max(newAngleUnc, TILT_DATA.angles[0]), TILT_DATA.angles[1]);

			this.#tiltData.current = newAngle;
			this.#tiltData.pos.x = event.clientX;
			this.#tiltData.pos.y = event.clientY;
			this.#mapControl.setTiltAngle(newAngle);

			return;
		}

		if (this.#mapControl && !this.#lockMap) {
			// zrovna tahame obdelnik pro bestfit
			if (this.#fitting) {
				// updatujeme obdelnik pro bestfit
				bestfitDiv.style.left = `${Math.max(Math.min(bestfitStartX, event.clientX), 0)}px`;
				bestfitDiv.style.top = `${Math.max(Math.min(bestfitStartY, event.clientY), 0)}px`;
				bestfitDiv.style.width = `${Math.abs(Math.min(Math.max(event.clientX, 0), parseInt(this.#canvas.style.width, 10)) - bestfitStartX)}px`;
				bestfitDiv.style.height = `${Math.abs(Math.min(Math.max(event.clientY, 0), parseInt(this.#canvas.style.height, 10)) - bestfitStartY)}px`;
			} else if (this.#dragging) {
				// drag on element
				this.#opts.onDragMove?.(this.#dragElementId, event);
			} else if (this.#activePointers.has(event.pointerId)) {
				this.#mapControl.onPointerMove(event.pointerId, event.clientX, event.clientY);
			} else if (this.#activePointers.size === 0 && event.pointerType === "mouse" && event.target === this.#canvas) {
				// send hover to map
				this.#mapControl?.onHover(event.clientX, event.clientY);
			}
		}
	}

	#eventUp(event: PointerEvent) {
		if (this.#tiltData.isActive) {
			this.#tiltData.isActive = false;

			return;
		}

		if (this.#mapControl && !this.#lockMap) {
			if (this.#dragging) {
				// drag end on element
				this.#opts.onDragEnd?.(this.#dragElementId, event);
				this.#dragging = false;
				this.#dragElementId = 0;
			} else if (this.#activePointers.has(event.pointerId) && this.#activePointers.size === 1) {
				const origEvent = this.#activePointers.get(event.pointerId);
				const dist = Math.sqrt(Math.pow(event.clientX - origEvent.clientX, 2) + Math.pow(event.clientY - origEvent.clientY, 2));

				if (dist <= this.#opts.clickDistance) {
					this.#mapControl.onPointerCancel(event.pointerId);
					this.#mapControl.onClick(event.clientX, event.clientY, { action: "click", event: event });
				} else if (this.#fitting) {
					const leftTop = this.coordsFromScreenPosition(parseInt(bestfitDiv.style.left, 10), parseInt(bestfitDiv.style.top, 10));
					const rightBottom = this.coordsFromScreenPosition(parseInt(bestfitDiv.style.left, 10) + parseInt(bestfitDiv.style.width, 10), parseInt(bestfitDiv.style.top, 10) + parseInt(bestfitDiv.style.height, 10));

					this.#opts.onBestfit?.([[leftTop.x, leftTop.y], [rightBottom.x, rightBottom.y]]);
				} else {
					this.#mapControl.onPointerUp(event.pointerId, event.clientX, event.clientY);
				}

				this.#fitting = false;
				bestfitDiv.remove();
				this.#lastPUEvents.push({ time: Date.now(), event });

				if (this.#lastPUEvents.length > MAX_PU_EVENTS) {
					this.#lastPUEvents.shift();
				}

				if (this.#lastPUEvents.length === MAX_PU_EVENTS) {
					const cur = this.#lastPUEvents[1];
					const prev = this.#lastPUEvents[0];
					const diff = cur.time - prev.time;

					if (diff <= this.#opts.dblClickTo && !this.#lastHoverElementId && !this.#lastHoverPoi) {
						const distDblClck = Math.sqrt(Math.pow(cur.event.clientX - prev.event.clientX, 2) + Math.pow(cur.event.clientY - prev.event.clientY, 2));

						if (distDblClck <= this.#opts.clickDistance) {
							this.#opts.onDblClick(event);
						}
					}
				}
			}
		}

		this.#activePointers.delete(event.pointerId);
	}

	#eventCancel(event: PointerEvent) {
		if (this.#mapControl && !this.#lockMap) {
			this.#mapControl.onPointerCancel(event.pointerId);
		}

		this.#activePointers.delete(event.pointerId);
	}

	#initMoveControls() {
		const elem = this.#opts.parentElem;

		// web
		elem.addEventListener("pointerdown", event => {
			this.#eventDown(event);
		});
		window.addEventListener("pointermove", event => {
			this.#eventMove(event);
		});
		window.addEventListener("pointerup", event => {
			this.#eventUp(event);
		});
		window.addEventListener("pointercancel", event => {
			this.#eventCancel(event);
		});

		/* eslint-disable-next-line */
		new WasmScroll(this, elem);
	}

	#initKeyboard() {
		window.addEventListener("keydown", this);
		window.addEventListener("keyup", this);
	}
}
